import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type Brand,
  type BrandSuggestion,
  type SocialConnection,
  type SocialProvider,
  type VoiceSuggestion,
} from "../../lib/api";
import { SocialIcon } from "../../components/SocialIcon";

const VOICES = [
  "professional",
  "friendly",
  "bold",
  "witty",
  "educational",
  "inspirational",
] as const;

const VOICE_DESCRIPTIONS: Record<string, string> = {
  professional:
    "Authoritative and polished — suits B2B, finance, and enterprise",
  friendly:
    "Warm and approachable — great for lifestyle, food, and consumer brands",
  bold: "Punchy and direct — built for campaigns that need to cut through noise",
  witty:
    "Clever and playful — works well for challenger brands and younger audiences",
  educational:
    "Informative and clear — ideal for health, tech, and instructional content",
  inspirational:
    "Motivating and aspirational — a natural fit for fitness, coaching, or mission-driven brands",
};

const ALL_PROVIDERS: SocialProvider[] = [
  "meta_instagram",
  "meta_facebook",
  "linkedin",
  "x",
  "bluesky",
];

const PROVIDER_META: Record<
  SocialProvider,
  { label: string; description: string }
> = {
  meta_instagram: {
    label: "Instagram",
    description: "Publish posts via Instagram Content Publishing API",
  },
  meta_facebook: {
    label: "Facebook",
    description: "Post to your Facebook Page via the Graph API",
  },
  linkedin: { label: "LinkedIn", description: "Share updates on LinkedIn" },
  x: { label: "X (Twitter)", description: "Post tweets via the X API" },
  bluesky: {
    label: "Bluesky",
    description: "Post to the Bluesky network via AT Protocol",
  },
};

const FIELD_LABELS: Record<string, string> = {
  name: "Brand name",
  industry: "Industry",
  targetAudience: "Target audience",
  voice: "Brand voice",
  colors: "Brand colors",
};

type SuggestedFields = Set<keyof Brand>;

export default function BrandEditor() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !brandId;

  const [brand, setBrand] = useState<Partial<Brand>>({
    name: "",
    voice: "friendly",
    industry: "",
    targetAudience: "",
    colors: { primary: "#4f6ef7", secondary: "#ffffff" },
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Whether the user has explicitly expanded the manual form on the new-brand flow
  const [manualMode, setManualMode] = useState(false);

  // ── Social connections (edit mode only) ────────────────────────────────────
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [socialSuccess, setSocialSuccess] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);

  // URL analyzer state
  const [analyzeUrl, setAnalyzeUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [siteBlocked, setSiteBlocked] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [suggestion, setSuggestion] = useState<BrandSuggestion | null>(null);
  const [suggestedFields, setSuggestedFields] = useState<SuggestedFields>(
    new Set(),
  );

  // Voice suggestion state
  const [suggestingVoice, setSuggestingVoice] = useState(false);
  const [voiceSuggestion, setVoiceSuggestion] =
    useState<VoiceSuggestion | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // Snapshot of the brand state before suggestions were applied, for undo
  const preApplySnapshot = useRef<Partial<Brand> | null>(null);

  useEffect(() => {
    if (!isNew && brandId) {
      api.brands
        .get(brandId)
        .then((r) => setBrand(r.brand))
        .catch(console.error);
    }
  }, [brandId, isNew]);

  // Load social connections for existing brands
  useEffect(() => {
    if (isNew || !brandId) return;
    setConnectionsLoading(true);
    api.social
      .list(brandId)
      .then(({ connections: c }) => setConnections(c))
      .catch(() => setSocialError("Failed to load connected channels"))
      .finally(() => setConnectionsLoading(false));
  }, [brandId, isNew]);

  // Handle OAuth callback — the provider redirects back here with ?connected=&brandId=
  useEffect(() => {
    if (isNew || !brandId) return;
    const connected = searchParams.get("connected") as SocialProvider | null;
    if (!connected) return;
    setSocialSuccess(
      `${PROVIDER_META[connected]?.label ?? connected} connected successfully.`,
    );
    // Reload connections to reflect the new link
    api.social
      .list(brandId)
      .then(({ connections: c }) => setConnections(c))
      .catch(console.error);
    // Clean the callback params from the URL without triggering a reload
    setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: keyof Brand, val: unknown) =>
    setBrand((prev) => ({ ...prev, [key]: val }));

  // ── URL analysis ────────────────────────────────────────────────────────────

  const applySuggestion = (s: BrandSuggestion) => {
    preApplySnapshot.current = { ...brand };
    const applied: SuggestedFields = new Set();
    setBrand((prev) => {
      const next = { ...prev };
      if (s.name) {
        next.name = s.name;
        applied.add("name");
      }
      if (s.industry) {
        next.industry = s.industry;
        applied.add("industry");
      }
      if (s.targetAudience) {
        next.targetAudience = s.targetAudience;
        applied.add("targetAudience");
      }
      if (s.voice) {
        next.voice = s.voice;
        applied.add("voice");
      }
      if (s.colors) {
        next.colors = s.colors;
        applied.add("colors");
      }
      return next;
    });
    setSuggestedFields(applied);
    setSuggestion(s);
    // Reveal the form after AI fills it
    setManualMode(true);
  };

  const handleAnalyzeUrl = async () => {
    const url = analyzeUrl.trim();
    if (!url) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setSuggestion(null);
    setSiteBlocked(false);

    try {
      const res = await api.brands.analyzeUrl(url);
      applySuggestion(res.suggestion);
    } catch (e: unknown) {
      const isBlocked = e instanceof ApiError && e.status === 403;
      if (isBlocked) {
        setSiteBlocked(true);
        setAnalyzeError(
          "This site blocked automated access. Copy some text from the page and paste it below — the AI will work from that instead.",
        );
      } else {
        setAnalyzeError(e instanceof Error ? e.message : "Analysis failed");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeText = async () => {
    if (!pastedText.trim()) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setSuggestion(null);

    try {
      const { suggestion: s } = await api.brands.analyzeText(pastedText);
      applySuggestion(s);
      setSiteBlocked(false);
    } catch (e) {
      setAnalyzeError(e instanceof Error ? e.message : "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSuggestVoice = async () => {
    setSuggestingVoice(true);
    setVoiceError(null);
    setVoiceSuggestion(null);
    try {
      const { suggestion: s } = await api.brands.suggestVoice({
        name: brand.name,
        industry: brand.industry,
        targetAudience: brand.targetAudience,
      });
      setVoiceSuggestion(s);
      set("voice", s.voice);
    } catch (e) {
      setVoiceError(e instanceof Error ? e.message : "Voice suggestion failed");
    } finally {
      setSuggestingVoice(false);
    }
  };

  const handleUndoSuggestions = () => {
    if (!preApplySnapshot.current) return;
    setBrand(preApplySnapshot.current);
    preApplySnapshot.current = null;
    setSuggestion(null);
    setSuggestedFields(new Set());
  };

  const handleClearSuggestions = () => {
    setSuggestion(null);
    setSuggestedFields(new Set());
    preApplySnapshot.current = null;
  };

  // ── Social connect / disconnect ─────────────────────────────────────────────

  const handleConnectPlatform = async (provider: SocialProvider) => {
    if (!brandId) return;
    setSocialError(null);
    try {
      // Return to this edit page after OAuth so the user lands back in context
      const { authUrl } = await api.social.startOAuth(
        provider,
        brandId,
        `/brands/${brandId}/edit`,
      );
      window.location.href = authUrl;
    } catch {
      setSocialError(
        `Failed to start ${PROVIDER_META[provider].label} connection.`,
      );
    }
  };

  const handleDisconnect = async (provider: SocialProvider) => {
    if (!brandId) return;
    setSocialError(null);
    setSocialSuccess(null);
    try {
      await api.social.disconnect(provider, brandId);
      setConnections((prev) => prev.filter((c) => c.provider !== provider));
      setSocialSuccess(`${PROVIDER_META[provider].label} disconnected.`);
    } catch {
      setSocialError(`Failed to disconnect ${PROVIDER_META[provider].label}.`);
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!brand.name?.trim()) return setError("Brand name is required");
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const result = await api.brands.create(brand);
        // Redirect to social setup for the new brand so the user can connect
        // its social accounts before starting to create campaigns.
        navigate(
          `/setup/social?brandId=${result.brand.brandId}&from=brand-create`,
        );
      } else {
        await api.brands.update(brandId!, brand);
        navigate("/brands");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasSuggestions = suggestedFields.size > 0;

  // Whether to show the full form (edit mode always; new mode after AI or manual trigger)
  const showForm = !isNew || manualMode || hasSuggestions;

  return (
    <div className="max-w-2xl">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/brands")}
          aria-label="Back to brands"
          className="text-gray-400 hover:text-gray-600 text-lg"
        >
          ←
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? "New brand" : "Edit brand"}
          </h1>
          {isNew && (
            <p className="text-sm text-gray-500 mt-0.5">
              Your brand profile shapes every piece of AI-generated content.
            </p>
          )}
        </div>
        {isNew && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0">
            <span className="bg-brand-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-semibold">
              1
            </span>
            <span className="font-medium text-gray-600">Brand</span>
            <span>→</span>
            <span className="rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 font-semibold">
              2
            </span>
            <span>Campaign</span>
            <span>→</span>
            <span className="rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 font-semibold">
              3
            </span>
            <span>Generate</span>
          </div>
        )}
      </div>

      {/* ── Hero AI Analyzer (new brand only) ── */}
      {isNew && (
        <div className="rounded-2xl bg-gradient-to-br from-brand-50 to-indigo-50 border border-brand-100 p-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">✦</span>
            <h2 className="text-lg font-bold text-gray-900">
              Let AI build your brand profile
            </h2>
            <span className="text-xs bg-brand-500 text-white px-2 py-0.5 rounded-full ml-auto font-medium">
              AI
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            Paste your website URL and the AI will fill in your brand name,
            industry, target audience, voice, and colors — ready to review and
            adjust before saving.
          </p>

          <div className="flex gap-2">
            <input
              type="url"
              className="input flex-1"
              value={analyzeUrl}
              onChange={(e) => setAnalyzeUrl(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && !analyzing && handleAnalyzeUrl()
              }
              placeholder="https://yourwebsite.com"
              aria-label="Website URL to analyze"
              autoFocus
            />
            <button
              onClick={handleAnalyzeUrl}
              disabled={analyzing || !analyzeUrl.trim()}
              aria-label="Analyze URL"
              className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 flex items-center gap-2"
            >
              {analyzing ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Analyzing…
                </>
              ) : (
                "Analyze →"
              )}
            </button>
          </div>

          {analyzeError && (
            <div className="mt-3">
              <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {analyzeError}
              </p>
              {siteBlocked && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-1.5">
                    Paste text from the page (copy from your browser, paste
                    here):
                  </p>
                  <textarea
                    className="input text-xs min-h-[100px] resize-y"
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste page copy, about text, product descriptions…"
                    aria-label="Paste page text for AI analysis"
                  />
                  <button
                    onClick={handleAnalyzeText}
                    disabled={analyzing || !pastedText.trim()}
                    className="btn-primary text-xs mt-2 disabled:opacity-50"
                  >
                    {analyzing ? "Analyzing…" : "Analyze pasted text"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Suggestion summary after AI fills in */}
          {hasSuggestions && suggestion && (
            <div className="mt-4 pt-4 border-t border-brand-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-brand-800 mb-0.5 flex items-center gap-1.5">
                    <span className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                      ✓
                    </span>
                    Brand profile filled in — review below
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed ml-5.5">
                    {suggestion.sourceNotes ??
                      "Fields were inferred from the page content. Review and edit anything before saving."}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2 ml-5.5">
                    {Array.from(suggestedFields).map((field) => (
                      <span
                        key={field}
                        className="text-xs bg-brand-100 text-brand-800 border border-brand-200 px-2 py-0.5 rounded-full"
                      >
                        ✦ {FIELD_LABELS[field] ?? field}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {preApplySnapshot.current && (
                    <button
                      onClick={handleUndoSuggestions}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Undo
                    </button>
                  )}
                  <button
                    onClick={handleClearSuggestions}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Manual entry escape hatch */}
          {!manualMode && !hasSuggestions && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setManualMode(true)}
                className="text-xs text-gray-400 hover:text-brand-600 transition-colors underline"
              >
                Skip — fill in manually ↓
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Edit mode: compact URL re-analyzer ── */}
      {!isNew && (
        <div className="card p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm">✦</span>
            <p className="text-sm font-semibold text-gray-800">
              Re-analyze from URL
            </p>
            <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full ml-auto">
              AI
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Paste a public URL to refresh your brand suggestions.
          </p>
          <div className="flex gap-2">
            <input
              type="url"
              className="input flex-1 text-sm"
              value={analyzeUrl}
              onChange={(e) => setAnalyzeUrl(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && !analyzing && handleAnalyzeUrl()
              }
              placeholder="https://yourwebsite.com"
              aria-label="Website URL to analyze"
            />
            <button
              onClick={handleAnalyzeUrl}
              disabled={analyzing || !analyzeUrl.trim()}
              aria-label="Analyze URL"
              className="btn-primary text-sm px-4 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {analyzing ? (
                <span className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                  Analyzing…
                </span>
              ) : (
                "Analyze"
              )}
            </button>
          </div>

          {analyzeError && (
            <div className="mt-3">
              <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {analyzeError}
              </p>
              {siteBlocked && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-1.5">
                    Paste text from the page:
                  </p>
                  <textarea
                    className="input text-xs min-h-[80px] resize-y"
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste page copy, about text, product descriptions…"
                  />
                  <button
                    onClick={handleAnalyzeText}
                    disabled={analyzing || !pastedText.trim()}
                    className="btn-primary text-xs mt-2 disabled:opacity-50"
                  >
                    {analyzing ? "Analyzing…" : "Analyze pasted text"}
                  </button>
                </div>
              )}
            </div>
          )}

          {hasSuggestions && suggestion && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-700 mb-0.5">
                    AI filled in the form
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {suggestion.sourceNotes ??
                      "Fields were inferred from the page content. Review and edit anything before saving."}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Array.from(suggestedFields).map((field) => (
                      <span
                        key={field}
                        className="text-xs bg-brand-50 text-brand-700 border border-brand-100 px-2 py-0.5 rounded-full"
                      >
                        {FIELD_LABELS[field] ?? field}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {preApplySnapshot.current && (
                    <button
                      onClick={handleUndoSuggestions}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Undo
                    </button>
                  )}
                  <button
                    onClick={handleClearSuggestions}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Save error ── */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
          {error}
        </p>
      )}

      {/* ── Form (always visible in edit mode; revealed in new mode after AI or manual trigger) ── */}
      {showForm && (
        <div className="card p-6 space-y-5">
          {/* Brand name */}
          <div>
            <label className="label">Brand name *</label>
            <input
              className={`input ${suggestedFields.has("name") ? "ring-2 ring-brand-200 border-brand-300" : ""}`}
              value={brand.name ?? ""}
              onChange={(e) => {
                set("name", e.target.value);
                setSuggestedFields((prev) => {
                  const n = new Set(prev);
                  n.delete("name");
                  return n;
                });
              }}
              placeholder="Acme Co."
            />
            {suggestedFields.has("name") && (
              <p className="text-xs text-brand-600 mt-1">✦ Suggested by AI</p>
            )}
          </div>

          {/* Industry */}
          <div>
            <label className="label">Industry</label>
            <input
              className={`input ${suggestedFields.has("industry") ? "ring-2 ring-brand-200 border-brand-300" : ""}`}
              value={brand.industry ?? ""}
              onChange={(e) => {
                set("industry", e.target.value);
                setSuggestedFields((prev) => {
                  const n = new Set(prev);
                  n.delete("industry");
                  return n;
                });
              }}
              placeholder="e.g. Food & Beverage, SaaS, Retail"
            />
            {suggestedFields.has("industry") && (
              <p className="text-xs text-brand-600 mt-1">✦ Suggested by AI</p>
            )}
          </div>

          {/* Target audience */}
          <div>
            <label className="label">Target audience</label>
            <textarea
              className={`input min-h-[80px] resize-none ${suggestedFields.has("targetAudience") ? "ring-2 ring-brand-200 border-brand-300" : ""}`}
              value={brand.targetAudience ?? ""}
              onChange={(e) => {
                set("targetAudience", e.target.value);
                setSuggestedFields((prev) => {
                  const n = new Set(prev);
                  n.delete("targetAudience");
                  return n;
                });
              }}
              placeholder="Age range, interests, pain points…"
            />
            {suggestedFields.has("targetAudience") && (
              <p className="text-xs text-brand-600 mt-1">✦ Suggested by AI</p>
            )}
          </div>

          {/* Brand voice */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">
                Brand voice
                {suggestedFields.has("voice") && (
                  <span className="ml-2 text-xs text-brand-600 font-normal">
                    ✦ AI suggested
                  </span>
                )}
              </label>
              <button
                onClick={handleSuggestVoice}
                disabled={
                  suggestingVoice ||
                  (!brand.name && !brand.industry && !brand.targetAudience)
                }
                aria-label="Ask AI to suggest a brand voice"
                className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {suggestingVoice ? (
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 border border-brand-400 border-t-transparent rounded-full animate-spin inline-block" />
                    Suggesting…
                  </span>
                ) : (
                  "✦ Suggest voice"
                )}
              </button>
            </div>

            {voiceError && (
              <p className="text-xs text-red-600 mb-2">{voiceError}</p>
            )}
            {voiceSuggestion && (
              <p className="text-xs text-brand-600 bg-brand-50 rounded-lg px-3 py-1.5 mb-2">
                ✦ {voiceSuggestion.reason}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {VOICES.map((v) => (
                <button
                  key={v}
                  onClick={() => {
                    set("voice", v);
                    setSuggestedFields((prev) => {
                      const n = new Set(prev);
                      n.delete("voice");
                      return n;
                    });
                    setVoiceSuggestion(null);
                  }}
                  title={VOICE_DESCRIPTIONS[v]}
                  aria-label={`${v} — ${VOICE_DESCRIPTIONS[v]}`}
                  className={`px-3 py-2 rounded-lg text-sm border capitalize transition-all text-left ${
                    brand.voice === v
                      ? "bg-brand-500 text-white border-brand-500 shadow-sm"
                      : "border-gray-200 text-gray-600 hover:border-brand-300 hover:bg-brand-50"
                  }`}
                >
                  <span className="font-medium">{v}</span>
                  <span
                    className={`block text-xs mt-0.5 leading-snug ${brand.voice === v ? "text-white/75" : "text-gray-400"}`}
                  >
                    {VOICE_DESCRIPTIONS[v].split("—")[0].trim()}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="label">
                Primary color
                {suggestedFields.has("colors") && (
                  <span className="ml-2 text-xs text-brand-600 font-normal">
                    ✦ AI suggested
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={brand.colors?.primary ?? "#4f6ef7"}
                  onChange={(e) => {
                    set("colors", { ...brand.colors, primary: e.target.value });
                    setSuggestedFields((prev) => {
                      const n = new Set(prev);
                      n.delete("colors");
                      return n;
                    });
                  }}
                  aria-label="Primary brand color"
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                />
                <span className="text-sm text-gray-500 font-mono">
                  {brand.colors?.primary}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <label className="label">Secondary color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={brand.colors?.secondary ?? "#ffffff"}
                  onChange={(e) => {
                    set("colors", {
                      ...brand.colors,
                      secondary: e.target.value,
                    });
                    setSuggestedFields((prev) => {
                      const n = new Set(prev);
                      n.delete("colors");
                      return n;
                    });
                  }}
                  aria-label="Secondary brand color"
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                />
                <span className="text-sm text-gray-500 font-mono">
                  {brand.colors?.secondary}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Connected Channels (edit mode only) ── */}
      {!isNew && (
        <div className="card p-6 mt-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-gray-900">
              Connected Channels
            </h2>
            <span className="text-xs text-gray-400">
              Connections are scoped to this brand
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Connect the social accounts that MediaDingo should publish to on
            behalf of <strong>{brand.name || "this brand"}</strong>.
          </p>

          {/* Feedback banners */}
          {socialError && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {socialError}
            </div>
          )}
          {socialSuccess && (
            <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              {socialSuccess}
            </div>
          )}

          {connectionsLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-3">
              {ALL_PROVIDERS.map((provider) => {
                const meta = PROVIDER_META[provider];
                const conn = connections.find((c) => c.provider === provider);
                return (
                  <div
                    key={provider}
                    className="flex items-center justify-between gap-4 py-3 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <SocialIcon provider={provider} size={36} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {meta.label}
                          {conn && (
                            <span className="ml-2 text-xs text-green-600 font-normal">
                              Connected
                              {conn.displayName ? ` — ${conn.displayName}` : ""}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {meta.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {conn ? (
                        <button
                          onClick={() => handleDisconnect(provider)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnectPlatform(provider)}
                          className="text-xs font-medium px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Actions ── */}
      {showForm && (
        <div className="flex gap-3 mt-6 items-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving
              ? "Saving…"
              : isNew
                ? "Create brand & set up campaigns →"
                : "Save changes"}
          </button>
          <button onClick={() => navigate("/brands")} className="btn-secondary">
            Cancel
          </button>
          {isNew && (
            <p className="text-xs text-gray-400 ml-1">
              You'll set up your first campaign next.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
